import api from "./api";

const TOKEN_KEY = "nexus_token";
const SESSION_KEY = "nexus_session";

api.defaults.withCredentials = true;

export async function login(email, password) {
    const response = await api.post(
        `/auth/login`,
        {
            email,
            password
        },
        {
            withCredentials: true
        }
    );

    return response.data;
}

export async function register(payload) {
    const response = await api.post(
        `/auth/register`,
        payload,
        {
            withCredentials: true
        }
    );

    return response.data;
}

export async function loginWithGoogle(idToken) {
    const response = await api.post(
        `/auth/google`,
        {
            idToken
        },
        {
            withCredentials: true
        }
    );

    return response.data;
}

export async function getCurrentUser() {
    const response = await api.get(
        `/auth/me`,
        {
            withCredentials: true
        }
    );

    return response.data;
}

export async function logout() {
    try {
        await api.post(
            `/auth/logout`,
            {},
            {
                withCredentials: true
            }
        );
    } catch (error) {
        console.warn("Logout request failed, clearing local session anyway.", error);
    } finally {
        clearSession();

        delete api.defaults.headers.common.Authorization;

        localStorage.removeItem("nexus_token");
        localStorage.removeItem("nexus_session");

        localStorage.removeItem("snx_user");
        localStorage.removeItem("user");
        localStorage.removeItem("authUser");

        sessionStorage.removeItem("nexus_token");
        sessionStorage.removeItem("nexus_session");

        sessionStorage.removeItem("snx_user");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("authUser");
    }
}

export function saveSession(data) {
    if (!data) return;

    if (data?.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
    }

    localStorage.setItem(SESSION_KEY, JSON.stringify(data));
}

export function getToken() {
    return localStorage.getItem(TOKEN_KEY);
}

export function getSession() {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;

    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

export function isSuperAdminUser() {
    return !!getSession()?.isSuperAdmin;
}

export function clearSession() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(SESSION_KEY);

    localStorage.removeItem("snx_user");
    localStorage.removeItem("user");
    localStorage.removeItem("authUser");

    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(SESSION_KEY);

    sessionStorage.removeItem("snx_user");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("authUser");

    delete api.defaults.headers.common.Authorization;
}

export async function refreshSessionFromServer() {
    try {
        const me = await getCurrentUser();

        const existing = getSession() || {};
        const merged = {
            ...existing,
            ...me,
            token: existing.token
        };

        saveSession(merged);
        return merged;
    } catch (error) {
        const hasToken = !!getToken();

        if (!hasToken) {
            clearSession();
        }

        throw error;
    }
}

export function isAuthenticated() {
    return !!getSession() || !!getToken();
}

export function getUserRole() {
    const session = getSession();

    if (session?.role) return session.role;
    if (session?.isSuperAdmin) return "SuperAdmin";

    return "AccountOwner";
}

export function hasRole(...roles) {
    const role = getUserRole();
    return roles.some((x) => String(x).toLowerCase() === String(role).toLowerCase());
}

export function canManageSeeds() {
    return hasRole("SuperAdmin");
}

export function canManagePlatformSettings() {
    return hasRole("SuperAdmin");
}

export function canViewLeads() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin",
        "AgencySupport",
        "AgencyViewer",
        "AccountOwner",
        "ClientAdmin",
        "ClientEditor",
        "Viewer"
    );
}

export function canManageLeads() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin",
        "AgencySupport",
        "AccountOwner",
        "ClientAdmin"
    );
}

export function canEditProjectContent() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin",
        "AgencySupport",
        "AccountOwner",
        "ClientAdmin",
        "ClientEditor"
    );
}

export function canUpdateLiveSite() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin",
        "AccountOwner",
        "ClientAdmin"
    );
}

export function canRollbackDeployment() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin",
        "AccountOwner"
    );
}

export function canUseProjectDiagnostics() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin"
    );
}

export function canPreviewProjectUpgrade() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin",
        "AccountOwner",
        "ClientAdmin"
    );
}

export function canApplyProjectUpgrade() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin"
    );
}

export function canUseProjectAiStudio() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin"
    );
}

export function canRepairCrmFromSeed() {
    return hasRole("SuperAdmin");
}

export function canAssignProjectAccounts() {
    return hasRole("SuperAdmin", "AgencyOwner", "AgencyAdmin");
}

export function canRepairCrm() {
    return hasRole("SuperAdmin");
}

export function canViewAccounts() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin",
        "AgencySupport",
        "AgencyViewer",
        "AccountOwner",
        "ClientAdmin"
    );
}

export function canManageAccounts() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin",
        "AccountOwner",
        "ClientAdmin"
    );
}

export function isAgencyUser() {
    return hasRole(
        "AgencyOwner",
        "AgencyAdmin",
        "AgencySupport",
        "AgencyViewer"
    );
}

export function isPlatformUser() {
    return hasRole("SuperAdmin") || !!getSession()?.isSuperAdmin;
}

export function isAgencyOperator() {
    return hasRole(
        "AgencyOwner",
        "AgencyAdmin"
    );
}

export function canPreviewPremiumUpgrade() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin",
        "AccountOwner",
        "ClientAdmin"
    );
}

export function canApplyPremiumUpgrade() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin"
    );
}

export function canUseAiStudio() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin"
    );
}

export function canUseVariantTools() {
    return hasRole(
        "SuperAdmin",
        "AgencyOwner",
        "AgencyAdmin"
    );
}

export function getAccountType() {
    const session = getSession();

    return (
        session?.accountType ||
        session?.AccountType ||
        (session?.isSuperAdmin ? "Platform" : "Client")
    );
}

export function getWorkspaceLabel() {
    const accountType = getAccountType();

    if (hasRole("SuperAdmin")) return "Platform Admin";
    if (String(accountType).toLowerCase() === "agency" || isAgencyUser()) return "Agency Workspace";

    if (hasRole("ClientAdmin")) return "Client Admin";
    if (hasRole("ClientEditor")) return "Client Editor";
    if (hasRole("Viewer")) return "Viewer";

    return "Client Workspace";
}

export function getBrandDisplayName() {
    const session = getSession();

    return (
        session?.displayBrandName ||
        session?.workspaceName ||
        session?.companyName ||
        session?.businessName ||
        session?.name ||
        "Nexus"
    );
}

export function getBrandInitials() {
    const name = getBrandDisplayName();

    return String(name || "SN")
        .split(" ")
        .filter(Boolean)
        .map((x) => x[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
}